import { INITIAL_FILTERS, type ConnectorType, type FilterState } from '@/types/charger';

const STORAGE_KEY = 'evtolto.filters';
const CONN = new Set<ConnectorType>(['Type2', 'CCS', 'CHAdeMO']);
const OPERATOR_CODE = /^[A-Za-z0-9_-]{1,32}$/;
const MAX_OPERATOR_CODES = 8;

/** Egy organization_code, vagy vesszővel több (egy márka több nyilvántartási kódja). */
export function sanitizeOperatorParam(raw: string): string {
  const codes: string[] = [];
  for (const part of raw.split(',')) {
    const code = part.trim();
    if (!OPERATOR_CODE.test(code) || codes.includes(code)) continue;
    codes.push(code);
    if (codes.length >= MAX_OPERATOR_CODES) break;
  }
  return codes.join(',');
}

export function matchesOperator(filterOperator: string, operatorCode: string): boolean {
  if (!filterOperator) return true;
  if (!filterOperator.includes(',')) return operatorCode === filterOperator;
  return filterOperator.split(',').includes(operatorCode);
}

export function filtersFromSearch(sp: URLSearchParams): FilterState | null {
  if (!sp.has('kw') && !sp.has('conn') && !sp.has('free') && !sp.has('cpo')) {
    return null;
  }
  const kwRaw = Number(sp.get('kw') || 0);
  const minKw = Number.isFinite(kwRaw) && kwRaw > 0 ? kwRaw : 0;
  const connectors = (sp.get('conn') || '')
    .split(',')
    .map((c) => c.trim())
    .filter((c): c is ConnectorType => CONN.has(c as ConnectorType));
  return {
    minKw,
    connectors,
    onlyAvailable: sp.get('free') === '1',
    operator: sanitizeOperatorParam(sp.get('cpo') || ''),
  };
}

export function applyFiltersToSearch(sp: URLSearchParams, f: FilterState): void {
  if (f.minKw > 0) sp.set('kw', String(f.minKw));
  else sp.delete('kw');
  if (f.connectors.length) sp.set('conn', f.connectors.join(','));
  else sp.delete('conn');
  if (f.onlyAvailable) sp.set('free', '1');
  else sp.delete('free');
  if (f.operator) sp.set('cpo', f.operator);
  else sp.delete('cpo');
}

export function readStoredFilters(): FilterState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FilterState>;
    const minKw = Number(parsed.minKw) || 0;
    const connectors = Array.isArray(parsed.connectors)
      ? parsed.connectors.filter((c): c is ConnectorType => CONN.has(c as ConnectorType))
      : [];
    return {
      minKw: minKw > 0 ? minKw : 0,
      connectors,
      onlyAvailable: Boolean(parsed.onlyAvailable),
      operator: typeof parsed.operator === 'string' ? sanitizeOperatorParam(parsed.operator) : '',
    };
  } catch {
    return null;
  }
}

export function writeStoredFilters(f: FilterState): void {
  try {
    if (
      f.minKw <= 0 &&
      f.connectors.length === 0 &&
      !f.onlyAvailable &&
      f.operator === ''
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(f));
  } catch {
    /* private mode */
  }
}

export function loadFilters(): FilterState {
  if (typeof window === 'undefined') return INITIAL_FILTERS;
  return filtersFromSearch(new URLSearchParams(window.location.search)) ?? readStoredFilters() ?? INITIAL_FILTERS;
}

export function persistFilters(f: FilterState): void {
  if (typeof window === 'undefined') return;
  writeStoredFilters(f);
  const url = new URL(window.location.href);
  applyFiltersToSearch(url.searchParams, f);
  const next = url.pathname + url.search + url.hash;
  const cur = window.location.pathname + window.location.search + window.location.hash;
  if (next !== cur) {
    window.history.replaceState(window.history.state, '', next);
  }
}
